import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import usePresence from './usePresence';
import useTitleBadge from './useTitleBadge';
import BoardGate from './BoardGate';
import { chime } from './ops/inboxAlarm';
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { ToastStack, setToastLayerOffset, useToastStack } from '@repo/common';
import { ReactComponent as TaskIcon } from '@repo/common/assets/icons/20px/icon_20px_clipboard_outline.svg';
import { ReactComponent as DashboardIcon } from '@repo/common/assets/icons/20px/icon_20px_dashboard_outline.svg';
import { ReactComponent as FolderIcon } from '@repo/common/assets/icons/20px/icon_20px_folder_outline.svg';
import { ReactComponent as SettingIcon } from '@repo/common/assets/icons/20px/icon_20px_setting_outline.svg';
import { ReactComponent as ChevronIcon } from '@repo/common/assets/icons/20px/icon_20px_arrow-bottom.svg';
import { ReactComponent as DoubleLeftIcon } from '@repo/common/assets/icons/16px/icon_16px_doublearrow_left.svg';
import { ReactComponent as DoubleRightIcon } from '@repo/common/assets/icons/16px/icon_16px_doublearrow_right.svg';
import { ReactComponent as InboxIcon } from './icons/inbox.svg';
import {
  roleLabel,
  CP_SEEN_EVENT,
  checkinDeadline,
  checkinOpensAt,
  checkpointDueCount,
  loadSeenCheckpoints,
  cn,
  closedTipFor,
  isClosedFor,
  isTargetOf,
  isLeader,
  newRequestCount,
  presenceOf,
} from './model/derive';
import {
  KST,
  formatDue,
  formatMemberHours,
  fromInputValue,
  slotsOf,
  tzShortName,
} from './model/time';
import { WorkboardCtxOverride, useLiveWorkboard, useNow, useWorkboard } from './model/useWorkboard';
import { buildInbox, isToastable } from './model/inbox';
import { cpNeedsAttention, cpState } from './model/completeProject';
import { PEER_SCENARIOS, peerScenarioKey } from './model/mockCompleteProject';
import { CoachCompleteCard, CompleteProjectMenuItem } from './completeProject/entry';
import { CountBadge, MenuBadge, menuBadgeLabel } from './primitives';
import Coachmarks, { COACH_STEPS } from './Coachmarks';
import ScenarioModal from './ScenarioModal';
import OpsChat from './OpsChat';
import NoticeModal from './NoticeModal';
import { RingAvatar } from './WorkingHoursRing';
import TdSelect from './TdSelect';
import InboxPanel from './InboxPanel';
import useMedia, { MOBILE } from './useMedia';

const EMPTY_INBOX = { actions: [], activity: [], pending: 0, unread: 0 };
const RAIL_KEY = 'td-sidebar-rail';

/** « / » — 사이드바 접기 · 펴기 (디자인 시스템 16px 겹화살표) */
const RailIcon = ({ open, className }) =>
  open ? <DoubleRightIcon aria-hidden className={className} /> : <DoubleLeftIcon aria-hidden className={className} />;

/** ⋯ */
const MoreIcon = ({ className }) => (
  <svg viewBox='0 0 20 20' fill='currentColor' aria-hidden className={className}>
    <circle cx='5' cy='10' r='1.5' />
    <circle cx='10' cy='10' r='1.5' />
    <circle cx='15' cy='10' r='1.5' />
  </svg>
);

/** 접은 사이드바의 메뉴 — 아이콘만, 이름은 툴팁. 할 일이 있으면 점 */
const railItem = ({ isActive }) =>
  cn(
    'relative flex h-10 w-10 items-center justify-center rounded-lg',
    isActive ? 'bg-surface-green10 text-icon-primary' : 'text-icon-secondary hover:bg-grayscale-gray01',
  );
const RailDot = ({ show }) =>
  show ? (
    <span
      aria-hidden
      className='absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-functional-failed03 shadow-[0_0_0_2px_#ffffff]'
    />
  ) : null;

/** 1단 메뉴 (Dashboard · Inbox · Resources · Settings) */
const topItem = ({ isActive }) =>
  cn(
    'flex h-9 items-center gap-2.5 px-3 text-[14px] leading-[140%]',
    isActive
      ? 'rounded-full bg-surface-green10 font-medium text-text-primary [&>svg]:text-icon-primary'
      : 'rounded-lg text-text-tertiary hover:bg-green-deep03/[0.06]',
  );

/** Task 아래 2단 메뉴. 선택되면 왼쪽 가장자리에 초록 막대 */
const SubItem = ({ to, end, label, badge, forceActive = false, coach }) => (
  <NavLink
    to={to}
    end={end}
    data-coach={coach}
    className={({ isActive: routeActive }) => {
      const isActive = routeActive || forceActive;
      return cn(
        'relative flex h-[34px] items-center justify-between gap-2 rounded-lg pl-[42px] pr-2.5 text-[14px] leading-[140%]',
        isActive
          ? 'bg-surface-green10 font-medium text-text-primary'
          : 'text-text-tertiary hover:bg-grayscale-gray01',
      );
    }}
  >
    {({ isActive: routeActive }) => (
      <>
        {(routeActive || forceActive) && (
          <span
            aria-hidden
            className='absolute -left-3 top-1/2 -mt-2.5 h-5 w-[3px] rounded-r-sm bg-green-deep02'
          />
        )}
        <span>{label}</span>
        {badge}
      </>
    )}
  </NavLink>
);

/**
 * 개발용 시점 토글 — PM(리더) 화면과 일반 팀원 화면을 오가며 확인한다.
 * 운영 빌드에서는 보이지 않는다. 백엔드가 붙으면 로그인한 사람으로 정해지므로 지운다.
 */
const DevViewerToggle = ({ data, dispatch, peerKey, onPeer }) => {
  if (process.env.NODE_ENV === 'production') return null;
  const leader = data.members.find((m) => m.role === 'leader');
  const member =
    data.members.find((m) => m.id === 'lt') ??
    data.members.find((m) => m.role === 'member' && m.status === 'active');
  const options = [
    { label: 'PM', user: leader },
    { label: 'Member', user: member },
  ].filter((o) => o.user);
  return (
    <div className='fixed bottom-5 right-5 z-[900] flex items-center gap-1 rounded-full border border-green-deep03/20 bg-grayscale-gray00 p-1 text-[12px] shadow-4'>
      <span className='px-2 font-medium text-text-secondary'>Dev · View as</span>
      {options.map((o) => {
        const on = data.viewerId === o.user.id;
        return (
          <button
            key={o.label}
            type='button'
            aria-pressed={on}
            title={o.user.name}
            onClick={() => dispatch({ type: 'setViewer', viewerId: o.user.id })}
            className={cn(
              'h-7 rounded-full px-3 font-medium transition-colors',
              on
                ? 'bg-green-deep02 text-grayscale-gray00'
                : 'text-text-primary hover:bg-surface-green10',
            )}
          >
            {o.label}
          </button>
        );
      })}
      {onPeer && (
        <TdSelect
          aria-label='Peer review state'
          value={peerKey}
          onChange={(e) => onPeer(e.target.value)}
          className='h-7 w-auto rounded-full border-0 bg-surface-green10 px-2.5 font-medium text-text-primary'
        >
          {PEER_SCENARIOS.map((s) => (
            <option key={s.key} value={s.key}>
              Peer · {s.label}
            </option>
          ))}
        </TdSelect>
      )}
    </div>
  );
};

/**
 * 2days 워크보드 레이아웃 — 왼쪽 메뉴 + 본문.
 * 메뉴 구조는 MVP #13: Task(메인 보드) · 대시보드 · 알림 · 프로젝트 설정.
 * 메인웹 GNB·푸터는 AppInner 에서 /2days 경로일 때 숨긴다.
 */
const TwoDaysLayout = () => {
  // /2days/board/:slug — 실제 팀 보드 (서버). /2days/projects/:projectId — 목 데이터 파일럿 보드 (개발·시안 확인용)
  const { projectId = '', slug } = useParams();
  const live = !!slug;
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname, hash } = location;
  const boardBase = live ? `/2days/board/${slug}` : `/2days/projects/${projectId}`;
  // 목 데이터 전용: ?as=mp 로 다른 팀원 시점 보기. 처음 들어올 때만 읽어 메뉴를 옮겨 다녀도 유지된다
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewAs] = useState(() => searchParams.get('as'));
  const [peerKey, setPeerKey] = useState(
    () => peerScenarioKey(searchParams.get('peer')) ?? 'locked',
  );
  // ?tour=1 이면 코치마크 표시
  const touring = searchParams.get('tour') === '1';
  const endTour = useCallback(() => {
    setMenuOpen(false);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('tour');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);
  // 코치마크가 다른 화면을 가리키면(6/6 Resources) 그 화면으로 옮긴다 — ?tour=1 은 그대로
  const enterCoachStep = useCallback(
    (step) => {
      if (!step.route) return;
      const to = `${boardBase}/${step.route}`;
      if (pathname !== to) navigate({ pathname: to, search: '?tour=1' });
    },
    [boardBase, pathname, navigate],
  );
  // 목 데이터 전용: ?at=2026-10-15T09:30 (KST) 으로 다른 시점 보기 — 대기 화면·작업 시간 밖 배너 확인용
  const [atOverride] = useState(() => searchParams.get('at'));
  const [initialPeer] = useState(peerKey);
  const mockBoard = useWorkboard(live ? null : projectId, live ? null : viewAs, initialPeer);
  const liveBoard = useLiveWorkboard(live ? slug : null);
  const { data, dispatch, isLoading, isError } = live ? liveBoard : mockBoard;
  const now = useNow(
    live ? null : atOverride ? fromInputValue(atOverride, KST)?.toISOString() : data?.frozenNow,
  );
  const [displayTz, setDisplayTz] = useState(KST);
  const [taskOpen, setTaskOpen] = useState(true);
  // 접은 사이드바(아이콘만) — 데스크탑 사이드바만. 이 브라우저에 기억한다
  const [railPref, setRailPref] = useState(() => {
    try {
      return window.localStorage.getItem(RAIL_KEY) === '1';
    } catch {
      return false;
    }
  });
  const setRail = (on) => {
    setRailPref(on);
    try {
      window.localStorage.setItem(RAIL_KEY, on ? '1' : '0');
    } catch {
      /* 저장 못 해도 지금 화면에서는 접힌다 */
    }
  };
  const [meMenu, setMeMenu] = useState(false); // 하단 내 줄의 ⋯ 메뉴
  // 모바일 메뉴 서랍 — 다른 화면으로 가면 닫는다
  const [menuOpen, setMenuOpen] = useState(false);
  const [notice, setNotice] = useState(null); // 공지 전문 모달 { id, text } — 훅은 이른 return 보다 위에
  const drawer = usePresence(menuOpen);
  useEffect(() => {
    setMenuOpen(false);
    setMeMenu(false);
  }, [pathname]);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => e.key === 'Escape' && setMenuOpen(false);
    window.addEventListener('keydown', onKey);
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [menuOpen]);
  useEffect(() => {
    if (hash === '#complete-project') navigate(`${boardBase}/complete-project`, { replace: true });
  }, [hash, boardBase, navigate]);
  const taskById = useMemo(() => new Map((data?.tasks ?? []).map((t) => [t.id, t])), [data?.tasks]);
  const memberById = useMemo(
    () => new Map((data?.members ?? []).map((m) => [m.id, m])),
    [data?.members],
  );

  // 본 Due now 체크포인트 (이 기기) — Checkpoints 페이지가 열리면 채워진다
  const [cpSeen, setCpSeen] = useState(() => new Set());
  const seenScope = data ? `${data.project.id}:${data.viewerId}` : '';
  useEffect(() => {
    if (!data) return undefined;
    setCpSeen(loadSeenCheckpoints(data));
    const on = (e) => setCpSeen(new Set(e.detail ?? []));
    window.addEventListener(CP_SEEN_EVENT, on);
    return () => window.removeEventListener(CP_SEEN_EVENT, on);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seenScope]);

  // 토스트 — 디자인 시스템 Toast 를 오른쪽 아래에 쌓는다 (성공 · 서버 거절 · 새 Inbox 알림)
  const toasts = useToastStack();
  const pushToast = toasts.push;
  // 성공 토스트 (Task sent · Handoff sent · Timeline saved …) — 3초 뒤 사라진다
  const okToast = useCallback(
    ({ title, sub }) => pushToast({ type: 'success', state: 'white', title, message: sub }),
    [pushToast],
  );

  // ── Inbox ──
  const wide = useMedia('(min-width: 1180px)');
  const narrow = useMedia(MOBILE);
  const inboxMode = wide ? 'push' : narrow ? 'sheet' : 'overlay';
  const [inboxOpen, setInboxOpen] = useState(false);
  const [inboxTab, setInboxTab] = useState('action');
  const [inboxExp, setInboxExp] = useState(null);
  const [arrivalTick, setArrivalTick] = useState(0);
  // 사용법 안내를 읽었는지 — 프로젝트 · 사람마다 브라우저에 기억
  const guideKey = data ? `2days-inbox-guides:${data.project.id}:${data.viewerId}` : null;
  const [guideRead, setGuideRead] = useState(() => new Set());
  useEffect(() => {
    if (!guideKey) return;
    try {
      setGuideRead(new Set(JSON.parse(window.localStorage.getItem(guideKey) ?? '[]')));
    } catch {
      setGuideRead(new Set());
    }
  }, [guideKey]);
  const readGuide = (key) =>
    setGuideRead((cur) => {
      if (cur.has(key)) return cur;
      const next = new Set([...cur, key]);
      try {
        window.localStorage.setItem(guideKey, JSON.stringify([...next]));
      } catch {
        // 기억하지 못해도 이번에는 읽음으로
      }
      return next;
    });
  const inbox = useMemo(
    () =>
      data && memberById.get(data.viewerId)
        ? buildInbox({
            data,
            now: live ? new Date() : now,
            memberById,
            taskById,
            tz: displayTz,
            guideRead,
          })
        : EMPTY_INBOX,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, now, live, memberById, taskById, displayTz, arrivalTick, guideRead],
  );
  const openInbox = useCallback(
    (tab = null, exp = null) => {
      setMenuOpen(false);
      setInboxTab(tab ?? (inbox.pending > 0 ? 'action' : 'activity'));
      setInboxExp(exp);
      setInboxOpen(true);
    },
    [inbox.pending],
  );
  const closeInbox = useCallback(() => {
    setInboxOpen(false);
    setInboxExp(null);
  }, []);
  const inboxPresence = usePresence(inboxOpen, 300);
  // 토스트 버튼은 그때의 화면 상태로 연다
  const actOnToastRef = useRef(null);

  // 체크포인트가 열리거나 마감되는 순간 Inbox 를 다시 계산한다
  useEffect(() => {
    if (!live || !data) return undefined;
    const me = memberById.get(data.viewerId);
    const t = Date.now();
    const next = (data.checkpoints ?? [])
      .filter((c) => isTargetOf(c, me))
      .flatMap((c) => [checkinOpensAt(c), checkinDeadline(c, me) + 1000])
      .filter((x) => x > t)
      .sort((a, b) => a - b)[0];
    if (!next) return undefined;
    const timer = window.setTimeout(
      () => setArrivalTick((x) => x + 1),
      Math.min(next - t + 500, 2_147_000_000),
    );
    return () => window.clearTimeout(timer);
  }, [live, data, memberById, arrivalTick]);

  // 새로 도착한 Needs action · 멘션 · 답글 — 소리 1회 + 토스트. 처음 불러온 것은 울리지 않는다
  const toastSeen = useRef({ scope: null, ids: null });
  const toastScope = data ? `${data.project.id}:${data.viewerId}` : null;
  const toastable = [...inbox.actions, ...inbox.activity].filter(isToastable);
  const toastKey = toastable.map((i) => i.id).join(',');
  useEffect(() => {
    if (!toastScope) return;
    const prev = toastSeen.current;
    const same = prev.scope === toastScope && prev.ids;
    if (same) {
      const fresh = toastable.filter((i) => !prev.ids.has(i.id));
      if (fresh.length) {
        chime();
        const item = fresh[0];
        pushToast({
          key: `inbox:${item.id}`,
          type: 'info',
          state: 'white',
          title: item.text,
          buttonLabel: item.btn ?? (item.tab === 'activity' && item.replyTo ? 'Reply' : 'Open'),
          onButtonClick: () => actOnToastRef.current?.(item),
          duration: 6000,
        });
      }
    }
    toastSeen.current = {
      scope: toastScope,
      ids: new Set([...(same ? prev.ids : []), ...toastable.map((i) => i.id)]),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toastKey, toastScope]);
  // 실제 보드 — 서버가 행동을 거절하면 알려 주고 화면은 서버 상태로 되돌린다
  const actionError = live ? liveBoard.actionError : null;
  useEffect(() => {
    if (actionError) pushToast({ key: 'action-error', type: 'error', state: 'white', title: actionError });
  }, [actionError, pushToast]);
  // 목 보드는 오른쪽 아래 개발용 시점 토글 위로 토스트를 띄운다
  useEffect(() => {
    setToastLayerOffset(live ? 0 : 56);
    return () => setToastLayerOffset(0);
  }, [live]);
  const closeToast = (id) => {
    toasts.close(id);
    if (id === 'action-error') liveBoard.clearActionError?.();
  };

  // /notifications 로 들어오면 Inbox 를 연다 · 태블릿·모바일은 다른 화면으로 가면 닫는다
  useEffect(() => {
    if (location.state?.openInbox) {
      openInbox();
      navigate(
        { pathname: location.pathname, search: location.search },
        { replace: true, state: null },
      );
    } else if (inboxMode !== 'push') {
      closeInbox();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // 태블릿 — 패널 밖을 누르면 닫는다
  useEffect(() => {
    if (!inboxOpen || inboxMode !== 'overlay') return undefined;
    const onDown = (e) => {
      if (e.target.closest?.('[data-inbox], [data-inbox-toggle], [role="dialog"]')) return;
      closeInbox();
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [inboxOpen, inboxMode, closeInbox]);

  /*
   * 시나리오 공개 — 워크보드에 처음 들어올 때 전체 화면 모달로 먼저 보여 준다.
   * 리더는 프로젝트 시작(T0), 팀원은 팀 시작(T0+1h) 부터. 그 전에 들어왔으면 시각이 되면 뜬다.
   * 한 번 본 뒤에는 이 기기에서 다시 띄우지 않는다 (서버에 열람 기록은 남기지 않는다). Resources 에서 언제든 다시 연다
   */
  const [scenario, setScenario] = useState({ open: false, first: false });
  const scenarioViewer = data?.members?.find((m) => m.id === data?.viewerId);
  const scenarioKey = data ? `2days-scenario-seen:${data.project.id}:${data.viewerId}` : null;
  const scenarioAt = data
    ? new Date(
        scenarioViewer && isLeader(scenarioViewer)
          ? data.project.startAt
          : (data.project.teamStartAt ?? new Date(data.project.startAt).getTime() + 3_600_000),
      )
    : null;
  const scenarioDue =
    !!data &&
    !!scenarioViewer &&
    scenarioViewer.role !== 'npc' &&
    !data.readOnly &&
    data.project.status !== 'draft' &&
    now >= scenarioAt;
  useEffect(() => {
    if (!scenarioDue || !scenarioKey) return;
    try {
      if (window.localStorage.getItem(scenarioKey)) return;
    } catch {
      // 저장소를 못 쓰면 이번에 한 번 보여 준다
    }
    setScenario({ open: true, first: true });
  }, [scenarioDue, scenarioKey]);
  const closeScenario = useCallback(() => {
    setScenario((cur) => ({ ...cur, open: false }));
    try {
      if (scenarioKey) window.localStorage.setItem(scenarioKey, '1');
    } catch {
      // 기억하지 못해도 닫는다
    }
  }, [scenarioKey]);
  const openScenario = useCallback(() => setScenario({ open: true, first: false }), []);

  const requests = data ? newRequestCount(data) : 0;
  // Checkpoints 페이지에서 본 Due now 체크포인트는 배지에서 뺀다 (Due now · Seen)
  const checkpointsDue = data ? checkpointDueCount(data, now, cpSeen) : 0;
  const inboxTotal = inbox.pending + inbox.unread;
  useTitleBadge(/\/complete-project\/?$/.test(pathname) ? 0 : inboxTotal);

  if (isLoading) {
    return <div className='min-h-screen bg-bg-twodays' />;
  }
  // 실제 보드를 열 수 없으면 입장 전 화면 — 서버에서 팀 링크를 확인하고 로그인 · Pre-join · 다른 계정을 안내한다
  if (live && (isError || !data)) return <BoardGate slug={slug} />;
  if (isError || !data) {
    // 목 보드 — 없는 프로젝트
    return (
      <div className='flex min-h-screen items-center justify-center bg-bg-twodays p-6 text-center'>
        <div className='flex flex-col items-center gap-3'>
          <p className='text-[18px] font-medium text-text-primary'>Project not found</p>
          <p className='text-[14px] text-text-secondary'>
            Check the link, or ask your team lead for a new invite.
          </p>
        </div>
      </div>
    );
  }

  const viewer = memberById.get(data.viewerId);
  const base = boardBase;
  // 운영진에게 메시지 — 읽기 전용 · 팀장 · 이탈한 팀원은 열지 않는다
  const organizerOpen =
    searchParams.get('organizer') === '1' &&
    !((live && data.readOnly) || data.project.status === 'ended') &&
    !!viewer &&
    viewer.role !== 'npc' &&
    viewer.status !== 'dropped';
  const closeOrganizer = () =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('organizer');
        return next;
      },
      { replace: true },
    );
  const openOrganizer = () =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('organizer', '1');
        return next;
      },
      { replace: true },
    );
  // 떠 있는 메신저 버튼 — 메시지를 보낼 수 있는 팀원에게만 (읽기 전용 · 팀장 · 이탈 · 끝난 프로젝트는 없다)
  const canMessage =
    !((live && data.readOnly) || data.project.status === 'ended') &&
    !!viewer &&
    viewer.role !== 'npc' &&
    viewer.status !== 'dropped';
  const cp = cpState(data, viewer, now);
  const ctx = {
    data,
    dispatch,
    now,
    displayTz,
    setDisplayTz,
    memberById,
    taskById,
    viewer,
    base,
    live,
    slug,
    cp,
    refresh: live ? liveBoard.refresh : null,
    // 스태프가 팀원이 아닌 팀 보드를 볼 때 · 끝난 프로젝트 — 행동 버튼·댓글 입력을 숨긴다 (서버도 막는다)
    readOnly: (live && !!data.readOnly) || data.project.status === 'ended',
    /** 성공 토스트 — toast({ title, sub }) */
    toast: okToast,
  };
  const devToggle = !live && (
    <DevViewerToggle
      data={data}
      dispatch={dispatch}
      peerKey={peerKey}
      onPeer={(key) => {
        setPeerKey(key);
        dispatch({ type: '@peerScenario', key });
      }}
    />
  );
  const viewerPresence = viewer ? presenceOf(viewer, now) : 'offline';
  const viewerHours = viewer
    ? `${formatMemberHours(viewer, displayTz, now)} ${tzShortName(displayTz, now)}`
    : '';
  /*
   * 시작 전 (Task Flows 10) — 대기 화면 없이 모두 워크보드에 들어온다.
   *   프로젝트 시작 전(draft): 모두 보기만 · "Project starts at 10:00 KST (in N)"
   *   리더 시작 ~ 팀 시작(+1시간): 리더는 평소처럼(팀원 알림은 팀 시작 때) · 팀원은 보기만 · "Your team starts at 11:00 KST"
   */
  const projectStart = new Date(data.project.startAt);
  const memberStart = new Date(data.project.teamStartAt ?? projectStart.getTime() + 3_600_000);
  const inMin = (at) => {
    const mins = Math.max(1, Math.ceil((at - now) / 60000));
    return mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins} min`;
  };
  const clock = (at) => `${formatDue(at, KST).split(', ')[1]} KST`;
  let startNotice = null;
  if (data.project.status === 'draft' || now < projectStart) {
    startNotice = {
      title: `Project starts at ${clock(projectStart)} (in ${inMin(projectStart)})`,
      sub: 'You can look around. Actions open then.',
    };
  } else if (now < memberStart && viewer && viewer.role !== 'npc') {
    startNotice = isLeader(viewer)
      ? {
          title: `Team joins in ${inMin(memberStart)}`,
          sub: 'Members aren’t notified until the team joins',
        }
      : {
          title: `Your team starts at ${clock(memberStart)} (in ${inMin(memberStart)})`,
          sub: 'You can look around. Actions open then.',
        };
  }
  // 행동이 열리는 시각 — 리더는 프로젝트 시작, 팀원은 팀 시작
  const opensAt = viewer && isLeader(viewer) ? projectStart : memberStart;
  ctx.actionsLocked = data.project.status === 'draft' || now < opensAt;
  ctx.lockTip = `Available from ${clock(opensAt)}`;
  // 프로젝트를 마쳤으면 체크인 · 댓글을 닫는다
  ctx.closed = isClosedFor(data, now);
  ctx.closedTip = closedTipFor(data);
  // 시나리오 — 내 시작 시각부터 Resources 에서 열 수 있다
  ctx.scenarioRevealed = data.project.status !== 'draft' && now >= opensAt;
  ctx.scenarioOpensAt = clock(opensAt);
  ctx.openScenario = openScenario;

  const inboxTarget = (go) => {
    if (go?.to === 'task' && taskById.get(go.taskId)) {
      return {
        pathname: `${base}/tasks/${go.taskId}`,
        focus: { focus: go.focus, commentId: go.commentId },
      };
    }
    if (go?.to === 'checkpoints') {
      return {
        pathname: `${base}/checkpoints`,
        focus: go.checkpointId ? { checkpointId: go.checkpointId } : null,
      };
    }
    if (go?.to === 'complete-project') return { pathname: `${base}/complete-project` };
    // 운영진 답장 — 페이지를 옮기지 않고 지금 화면에서 채팅 패널만 연다
    if (go?.to === 'organizer') return { pathname, search: '?organizer=1', stay: true };
    // 운영진 공지 — 지금 화면 위에 전문 모달
    if (go?.to === 'notice') return { pathname, stay: true, notice: { id: go.id, text: go.text } };
    return { pathname: `${base}/tasks/mine` };
  };
  const inboxHref = (item) => {
    const target = inboxTarget(item.go);
    return `${target.pathname}${target.search ?? ''}`;
  };
  const readInboxItem = (item) => {
    if (item.guide) {
      readGuide(item.guide);
      return;
    }
    if (!item.unread || !item.noteIds.length) return;
    const at = (live ? new Date() : now).toISOString();
    dispatch(
      item.noteIds.length > 1
        ? { type: 'readNotification', notificationIds: item.noteIds, at }
        : { type: 'readNotification', notificationId: item.noteIds[0], at },
    );
  };
  const openInboxItem = (item) => {
    readInboxItem(item);
    const target = inboxTarget(item.go);
    if (target.stay) {
      if (target.notice) setNotice(target.notice);
      else openOrganizer();
      if (inboxMode !== 'push') closeInbox();
      return;
    }
    navigate(
      { pathname: target.pathname, search: target.search ?? '' },
      { state: target.focus ? { inboxFocus: { ...target.focus, key: Date.now() } } : undefined },
    );
    if (inboxMode !== 'push') closeInbox();
  };
  // Dashboard 의 Need actions — Inbox 와 같은 항목 · 같은 이동
  ctx.inbox = inbox;
  ctx.openInbox = openInbox;
  ctx.openInboxItem = (item) =>
    item.btn ? openInbox(item.tab, item.id) : openInboxItem(item);
  const actOnToast = (item) => {
    toasts.close(`inbox:${item.id}`);
    if (item.btn || (item.tab === 'activity' && item.replyTo)) openInbox(item.tab, item.id);
    else openInboxItem(item);
  };
  actOnToastRef.current = actOnToast;
  const toastStack = <ToastStack toasts={toasts.toasts} onClose={closeToast} />;

  if (/\/complete-project\/?$/.test(pathname)) {
    return (
      <>
        <Outlet context={ctx} />
        {toastStack}
        {devToggle}
      </>
    );
  }

  const sidebarClass =
    'w-60 shrink-0 flex-col overflow-y-auto border-r border-green-deep03/[0.06] bg-grayscale-gray00 px-3 pb-3';
  // 코치마크는 메뉴 이름을 가리키므로 투어 중에는 펼쳐 둔다
  const rail = railPref && !touring;
  /**
   * 사이드바 — 데스크탑 사이드바와 모바일 서랍이 같이 쓴다.
   *   compact  접은 사이드바 (아이콘만 · 데스크탑만)
   *   canFold  « 접기 버튼을 보인다 (서랍에는 없다)
   */
  const renderSidebar = (compact, canFold) => (
    <>
      {compact ? (
        <div className='-mx-3 mb-3 flex flex-col items-center gap-2 border-b border-green-deep03/[0.08] py-4'>
          <span
            title={[data.project.title, data.project.teamName].filter(Boolean).join(' · ')}
            className='flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-green-deep02 text-[13px] font-medium text-grayscale-gray00'
          >
            2d
          </span>
          <button
            type='button'
            onClick={() => setRail(false)}
            aria-label='Expand sidebar'
            title='Expand sidebar'
            className='flex h-8 w-8 items-center justify-center rounded-lg text-icon-secondary hover:bg-green-deep03/[0.06]'
          >
            <RailIcon open className='h-4 w-4' />
          </button>
        </div>
      ) : (
        <div className='-mx-3 mb-3 flex h-[72px] items-center gap-2.5 border-b border-green-deep03/[0.08] pl-4 pr-2'>
          <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-green-deep02 text-[13px] font-medium text-grayscale-gray00'>
            2d
          </span>
          <span className='flex min-w-0 flex-1 flex-col'>
            <span className='truncate text-[15px] font-medium leading-[140%] text-text-primary'>
              {data.project.title}
            </span>
            <span className='text-[12px] leading-[140%] text-text-secondary'>
              {data.project.teamName}
            </span>
          </span>
          {canFold && (
            <button
              type='button'
              onClick={() => setRail(true)}
              aria-label='Collapse sidebar'
              title='Collapse sidebar'
              className='flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-icon-secondary hover:bg-green-deep03/[0.06]'
            >
              <RailIcon className='h-4 w-4' />
            </button>
          )}
        </div>
      )}

      {compact ? (
        <nav className='flex flex-col items-center gap-1' aria-label='Workboard'>
          <NavLink to={`${base}/dashboard`} title='Dashboard' aria-label='Dashboard' className={railItem}>
            <DashboardIcon className='h-5 w-5' />
          </NavLink>
          <NavLink
            to={`${base}/tasks/mine`}
            title={['Task', requests + checkpointsDue > 0 ? `${requests + checkpointsDue} to do` : '']
              .filter(Boolean)
              .join(' · ')}
            aria-label='Task'
            className={() => railItem({ isActive: /\/(tasks|checkpoints)(\/|$)/.test(pathname) })}
          >
            <TaskIcon className='h-5 w-5' />
            <RailDot show={requests + checkpointsDue > 0} />
          </NavLink>
          <button
            type='button'
            data-inbox-toggle
            aria-expanded={inboxOpen}
            aria-label='Inbox'
            title={`Inbox · ${inbox.pending} to do · ${inbox.unread} unread`}
            onClick={() => (inboxOpen ? closeInbox() : openInbox())}
            className={railItem({ isActive: inboxOpen })}
          >
            <InboxIcon className='h-5 w-5' />
            <RailDot show={inboxTotal > 0} />
          </button>
          <NavLink to={`${base}/resources`} title='Resources' aria-label='Resources' className={railItem}>
            <FolderIcon className='h-5 w-5' />
          </NavLink>
          {/* Complete project — 아이콘 자리만 보이게 자른다 */}
          {cp.eligible && (
            <div title='Complete project' className='relative w-10 overflow-hidden'>
              <CompleteProjectMenuItem cp={cp} to={`${base}/complete-project`} />
              <RailDot show={cpNeedsAttention(cp)} />
            </div>
          )}
        </nav>
      ) : (
        <nav className='flex flex-col gap-0.5' aria-label='Workboard'>
          <span className='flex h-7 items-center px-3 text-[12px] font-medium leading-[140%] text-text-secondary'>
            Work
          </span>
          {/* Dashboard 를 맨 위에 — 팀 전체 흐름을 먼저 보고 Task 로 내려간다 */}
          <NavLink to={`${base}/dashboard`} className={topItem}>
            <DashboardIcon className='h-5 w-5 shrink-0 text-icon-secondary' />
            Dashboard
          </NavLink>
          <button
            type='button'
            onClick={() => setTaskOpen((v) => !v)}
            aria-expanded={taskOpen}
            className='flex h-9 select-none items-center gap-2.5 rounded-lg px-3 text-[14px] font-medium leading-[140%] text-text-primary hover:bg-grayscale-gray01'
          >
            <TaskIcon className='h-5 w-5 shrink-0 text-icon-primary' />
            <span className='flex-1 text-left'>Task</span>
            {/* 접혀 있으면 할 일 수를 Task 에 (새 요청 + 지금 체크인할 체크포인트) */}
            {!taskOpen && requests + checkpointsDue > 0 && (
              <CountBadge
                count={requests + checkpointsDue}
                title={[
                  checkpointsDue ? 'Checkpoint due' : '',
                  requests ? `${requests} new task request${requests === 1 ? '' : 's'}` : '',
                ]
                  .filter(Boolean)
                  .join(' · ')}
              />
            )}
            <ChevronIcon
              className={cn(
                'h-5 w-5 shrink-0 text-icon-placeholder transition-transform',
                !taskOpen && '-rotate-90',
              )}
            />
          </button>
          {taskOpen && (
            <div className='relative flex flex-col gap-0.5'>
              {/* 하위 메뉴를 묶는 세로 안내선 */}
              <span
                aria-hidden
                className='absolute bottom-1 left-[21px] top-1 w-px bg-green-deep03/[0.12]'
              />
              {/* 태스크 상세(/tasks/:id)에서도 All tasks 가 선택된 채로 보인다 */}
              <SubItem
                to={`${base}/tasks`}
                end
                label='All tasks'
                forceActive={/\/tasks\/(?!mine)[^/]+$/.test(pathname)}
              />
              <SubItem
                to={`${base}/tasks/mine`}
                label='My tasks'
                coach='my-tasks'
                badge={
                  <CountBadge
                    count={requests}
                    title={`${requests} new task request${requests > 1 ? 's' : ''}`}
                  />
                }
              />
              <SubItem
                to={`${base}/checkpoints`}
                label='Checkpoints'
                coach='checkpoints'
                badge={<CountBadge count={checkpointsDue} title='Checkpoint due now' />}
              />
            </div>
          )}
          <button
            type='button'
            data-inbox-toggle
            aria-expanded={inboxOpen}
            onClick={() => (inboxOpen ? closeInbox() : openInbox())}
            className={topItem({ isActive: inboxOpen })}
          >
            <InboxIcon className='h-5 w-5 shrink-0 text-icon-secondary' />
            <span className='flex-1 text-left'>Inbox</span>
            <CountBadge
              count={inboxTotal}
              title={`${inbox.pending} to do · ${inbox.unread} unread`}
            />
          </button>
          <NavLink to={`${base}/resources`} data-coach='resources' className={topItem}>
            <FolderIcon className='h-5 w-5 shrink-0 text-icon-secondary' />
            Resources
          </NavLink>
          {cp.eligible && <CompleteProjectMenuItem cp={cp} to={`${base}/complete-project`} />}
        </nav>
      )}

      <div className={cn('mt-auto flex flex-col gap-0.5', compact && 'items-center')}>
        {/* 작업 시간 밖에 들어왔을 때 — 응답 시간·수락 대기는 멈추지만 마감은 흐른다 (MVP §10-2) */}
        {!compact && viewer && viewerPresence === 'offline' && (
          <div className='mb-2 rounded-xl bg-functional-warning01 px-3 py-2.5 text-[12px] leading-[150%] text-functional-warning04'>
            <p>
              You’re outside your work hours, so teammates see you as off hours. Working now? Update
              your work hours.
            </p>
            <Link
              to={`${base}/profile#work-hours`}
              className='mt-1 inline-block font-medium underline underline-offset-2'
            >
              Edit work hours
            </Link>
          </div>
        )}
        {compact ? (
          <NavLink to={`${base}/settings`} title='Settings' aria-label='Settings' className={railItem}>
            <SettingIcon className='h-5 w-5' />
          </NavLink>
        ) : (
          <NavLink to={`${base}/settings`} className={topItem}>
            <SettingIcon className='h-5 w-5 shrink-0 text-icon-secondary' />
            Settings
          </NavLink>
        )}
        {/* 동료 평가가 열리면 사이드바 아래에 카드 (오프라인 안내 아래 · 프로필 위) */}
        {!compact && cp.kind === 'open' && <CoachCompleteCard cp={cp} to={`${base}/complete-project`} />}
        {/* 내 줄 → Profile (작업 시간·툴·스택) — 아바타 · 이름 · 역할 · ⋯ 한 줄 */}
        {viewer && (
          <div className={cn('relative mt-2.5 flex items-center', !compact && 'gap-1')}>
            <NavLink
              to={`${base}/profile`}
              data-coach='profile'
              title={`${viewer.name} · Online${viewerPresence === 'offline' ? ' · Off hours' : ''} · works ${viewerHours}`}
              className={({ isActive }) =>
                cn(
                  'flex min-w-0 flex-1 items-center gap-2.5 rounded-lg transition-colors hover:bg-grayscale-gray01',
                  compact ? 'justify-center p-1' : 'px-1.5 py-1.5',
                  isActive && 'bg-surface-green10',
                )
              }
            >
              {/* 아바타 + 업무 가능 시간대 링. 접속 점은 링 위에 */}
              <RingAvatar
                member={viewer}
                photo={data.me?.photo}
                slots={slotsOf(viewer)}
                timezone={viewer.timezone}
                at={now}
                avatar={28}
                ring={36}
                textClass='text-[11px]'
                dot={
                  <span
                    aria-hidden
                    // 점 = 실제 접속 — 지금 보고 있으니 늘 Online
                    className='absolute -bottom-px -right-px z-[1] h-2.5 w-2.5 rounded-full bg-functional-success03 shadow-[0_0_0_2px_#ffffff]'
                  />
                }
              />
              {!compact && (
                <span className='flex min-w-0 flex-1 items-baseline gap-1.5'>
                  <span className='truncate text-[14px] font-medium leading-[140%] text-text-primary'>
                    {viewer.name}
                  </span>
                  <span className='shrink-0 text-[12px] leading-[140%] text-text-secondary'>
                    {roleLabel(viewer)}
                  </span>
                </span>
              )}
            </NavLink>
            {!compact && (
              <button
                type='button'
                aria-label='More'
                aria-haspopup='menu'
                aria-expanded={meMenu}
                onClick={() => setMeMenu((o) => !o)}
                className='flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-icon-secondary hover:bg-grayscale-gray01'
              >
                <MoreIcon className='h-5 w-5' />
              </button>
            )}
            {!compact && meMenu && (
              <>
                <button
                  type='button'
                  aria-label='Close menu'
                  onClick={() => setMeMenu(false)}
                  className='fixed inset-0 z-[40] cursor-default'
                />
                <div
                  role='menu'
                  className='absolute bottom-full right-0 z-[41] mb-1 flex w-48 flex-col rounded-xl border border-green-deep03/[0.12] bg-grayscale-gray00 p-1 shadow-popup-01'
                >
                  {[
                    ['Profile', `${base}/profile`],
                    ['Edit work hours', `${base}/profile#work-hours`],
                    ['Settings', `${base}/settings`],
                  ].map(([label, to]) => (
                    <Link
                      key={label}
                      role='menuitem'
                      to={to}
                      onClick={() => setMeMenu(false)}
                      className='flex h-9 items-center rounded-lg px-3 text-[14px] text-text-primary hover:bg-grayscale-gray01'
                    >
                      {label}
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className='flex min-h-screen bg-bg-twodays'>
      {/* 데스크탑 사이드바 */}
      {/* 태블릿(768~1179)은 220px, 데스크톱은 256px (Task Flows 7 · Responsive) */}
      <aside
        className={cn(
          sidebarClass,
          'sticky top-0 hidden h-screen tabletH:flex',
          rail ? 'w-16 tabletV:w-16' : 'w-[220px] tabletV:w-60',
        )}
      >
        {renderSidebar(rail, true)}
      </aside>

      {/* 모바일·태블릿 메뉴 서랍 — 데스크탑 사이드바와 같은 내용 */}
      {/* 열 때 왼쪽에서 밀려 들어오고, 닫을 때 왼쪽으로 빠진다 */}
      {drawer.mounted && (
        <div className='fixed inset-0 z-[950] tabletH:hidden'>
          <button
            type='button'
            aria-label='Close menu'
            onClick={() => setMenuOpen(false)}
            className={cn(
              'absolute inset-0 h-full w-full bg-grayscale-gray10/40 transition-opacity duration-300 motion-reduce:transition-none',
              drawer.shown ? 'opacity-100' : 'opacity-0',
            )}
          />
          <aside
            role='dialog'
            aria-modal='true'
            aria-label='Menu'
            className={cn(
              sidebarClass,
              'relative flex h-full max-w-[85vw] shadow-4 transition-transform duration-300 ease-out motion-reduce:transition-none',
              drawer.shown ? 'translate-x-0' : '-translate-x-full',
            )}
          >
            {renderSidebar(false, false)}
          </aside>
        </div>
      )}

      {inboxPresence.mounted && (
        <WorkboardCtxOverride.Provider value={ctx}>
          <InboxPanel
            inbox={inbox}
            mode={inboxMode}
            shown={inboxPresence.shown}
            tab={inboxTab}
            onTab={setInboxTab}
            exp={inboxExp}
            onExp={setInboxExp}
            onClose={closeInbox}
            onOpen={openInboxItem}
            onRead={readInboxItem}
            hrefOf={inboxHref}
          />
        </WorkboardCtxOverride.Provider>
      )}

      <div className='flex min-w-0 flex-1 flex-col'>
        {/* 모바일·태블릿: 사이드바 대신 위쪽 바 — Menu 를 누르면 서랍이 열린다 */}
        <header className='sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-green-deep03/[0.12] bg-grayscale-gray00 px-4 tabletH:hidden'>
          <button
            type='button'
            onClick={() => setMenuOpen(true)}
            aria-expanded={menuOpen}
            aria-label={menuBadgeLabel(inboxTotal, cpNeedsAttention(cp))}
            className='relative -ml-2 flex h-9 shrink-0 items-center rounded-lg px-2 text-[14px] font-medium text-text-primary hover:bg-grayscale-gray01'
          >
            Menu
            <MenuBadge count={inboxTotal} status={cpNeedsAttention(cp)} />
          </button>
          <span className='flex min-w-0 items-baseline gap-2'>
            <span className='shrink-0 text-[14px] font-medium text-text-primary'>
              2 days project
            </span>
            <span className='truncate text-[12px] text-text-secondary'>
              {[data.project.title, data.project.teamName].filter(Boolean).join(' · ')}
            </span>
          </span>
        </header>

        {/* 본문은 1440px 까지만 넓어진다 — 그보다 넓으면 오른쪽이 빈다 */}
        <main className='w-full max-w-[1440px] flex-1 px-4 py-6 sm:px-8 lg:px-12 lg:py-10'>
          {live && data.readOnly && (
            <p className='mb-6 rounded-xl bg-functional-accent01 px-4 py-3 text-[13px] text-functional-accent04'>
              You’re viewing this team as staff. It’s read-only — you can act only on teams where
              you hold or receive a task.
            </p>
          )}
          {startNotice && (
            <div
              role='status'
              className='mb-6 flex flex-col gap-0.5 rounded-xl bg-surface-green10 px-4 py-3 text-[14px] shadow-[inset_0_0_0_1px_rgba(0,158,79,0.2)]'
            >
              <span className='font-medium text-text-primary'>{startNotice.title}</span>
              <span className='text-text-secondary'>{startNotice.sub}</span>
            </div>
          )}
          <Outlet context={ctx} />
        </main>
      </div>
      {/* Ops 메신저 — 떠 있는 버튼 · Team 패널 버튼 · Inbox 의 운영진 답장 알림(?organizer=1) 어디서 열어도 여기 하나 */}
      <NoticeModal notice={notice} slug={slug} live={live} onClose={() => setNotice(null)} />
      {canMessage && (
        <WorkboardCtxOverride.Provider value={ctx}>
          <OpsChat
            open={organizerOpen}
            onOpen={openOrganizer}
            onClose={closeOrganizer}
            taskId={searchParams.get('task')}
            lifted={narrow && /\/tasks\/[^/]+$/.test(pathname)}
          />
        </WorkboardCtxOverride.Provider>
      )}
      <ScenarioModal
        open={scenario.open}
        first={scenario.first}
        project={data.project}
        onClose={closeScenario}
      />
      {/* 첫 진입이면 시나리오를 닫은 뒤에 코치마크가 이어진다 */}
      {touring && !scenario.open && (
        <Coachmarks
          steps={
            cp.eligible ? COACH_STEPS : COACH_STEPS.filter((s) => s.key !== 'complete-project')
          }
          onDone={endTour}
          onNeedMenu={setMenuOpen}
          onEnter={enterCoachStep}
        />
      )}
      {toastStack}
      {devToggle}
    </div>
  );
};

export default TwoDaysLayout;
